/**
 * Image dropzone with previews. Validates type/size against marcConfig.limits.
 */

import { config, t } from '../config.js';
import { byHook, formatBytes } from '../dom.js';

export const createDropzone = (root, { onChange, onError } = {}) => {
    const dropzone = byHook('dropzone', root);
    const fileInput = byHook('file-input', root);
    const previews = byHook('previews', root);

    let files = [];

    const limits = config.limits;

    const dispatch = () => onChange?.(files.slice());

    const renderPreview = (file, index) => {
        const li = document.createElement('li');
        li.className = 'marc__preview';
        li.dataset.index = String(index);

        const img = document.createElement('img');
        img.className = 'marc__preview-image';
        img.alt = file.name;
        img.src = URL.createObjectURL(file);
        img.addEventListener('load', () => URL.revokeObjectURL(img.src), { once: true });

        const meta = document.createElement('div');
        meta.className = 'marc__preview-meta';
        meta.innerHTML = `
            <span class="marc__preview-name"></span>
            <span class="marc__preview-size">${formatBytes(file.size)}</span>
        `;
        meta.querySelector('.marc__preview-name').textContent = file.name;

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'marc__preview-remove';
        remove.setAttribute('aria-label', t('removeFile', 'Remove'));
        remove.textContent = '×';
        remove.addEventListener('click', () => {
            files.splice(index, 1);
            render();
            dispatch();
        });

        li.append(img, meta, remove);
        return li;
    };

    const render = () => {
        previews.replaceChildren(...files.map((file, index) => renderPreview(file, index)));
    };

    const accept = (incoming) => {
        const errors = [];
        for (const file of incoming) {
            if (files.length >= limits.maxFiles) {
                errors.push(t('tooManyFiles', `Up to ${limits.maxFiles} files`).replace('%d', limits.maxFiles));
                break;
            }
            if (!limits.allowedMimes.includes(file.type)) {
                errors.push(t('fileWrongType', `${file.name} is not allowed`).replace('%s', file.name));
                continue;
            }
            if (file.size > limits.maxFileSize) {
                errors.push(t('fileTooLarge', `${file.name} is too large`).replace('%s', file.name));
                continue;
            }
            files.push(file);
        }

        if (errors.length) onError?.(errors[0]);

        render();
        dispatch();
    };

    dropzone.addEventListener('click', () => fileInput.click());
    dropzone.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            fileInput.click();
        }
    });

    ['dragenter', 'dragover'].forEach((evt) => {
        dropzone.addEventListener(evt, (event) => {
            event.preventDefault();
            dropzone.classList.add('marc__dropzone--active');
        });
    });

    ['dragleave', 'drop', 'dragend'].forEach((evt) => {
        dropzone.addEventListener(evt, (event) => {
            event.preventDefault();
            dropzone.classList.remove('marc__dropzone--active');
        });
    });

    dropzone.addEventListener('drop', (event) => {
        accept(event.dataTransfer?.files ?? []);
    });

    fileInput.addEventListener('change', () => {
        accept(fileInput.files ?? []);
        fileInput.value = '';
    });

    return {
        getFiles: () => files.slice(),
        clear() {
            files = [];
            render();
            dispatch();
        },
    };
};
